import { useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { PlateElement, useEditorRef, useSelected } from 'platejs/react';
import styles from './HrElement.module.scss';

// Custom <hr> element for Plate.
//
// Why a custom component (vs the default `PlateElement as="hr"`):
//   React asserts that void HTML elements (`<hr>`, `<br>`, `<img>`, ...) MUST
//   NOT have `children`. Plate's `PlateElement` always renders `props.children`
//   inside the chosen tag — that's the slate void marker `<span>`. When the
//   parent tag is `<hr>`, React throws:
//     "hr is a void element tag and must neither have `children` nor use
//     `dangerouslySetInnerHTML`."
//
// Fix: render `<hr/>` inside a non-void wrapper (`<div>`), and let the slate
// void marker sit as a SIBLING of the `<hr/>` inside the wrapper. The outer
// PlateElement renders as the default `<div>` (no `as` override).
//
// SELECTION: because the inner `<hr/>` lives in a `contentEditable={false}`
// container, a mouse click on it does NOT naturally land a Slate selection on
// the void node (Slate's selection logic looks for an editable text target).
// Without explicit handling, the divider is "unselectable" — you can't click
// it to give it focus, can't press Backspace/Delete on it, can't see a hover/
// focus ring. The mousedown handler below resolves the node's path and forces
// Slate's selection onto it; `useSelected` then drives the visual ring.
export function HrElement(props: any) {
    const editor = useEditorRef();
    // `useSelected` reads slate-react's per-element selection state. True when
    // the current Slate selection is anchored on this node — for void nodes,
    // that's typically the result of an explicit `editor.tf.select(path)`.
    const selected = useSelected();

    const handleSelect = useCallback(
        (e: React.MouseEvent) => {
            // Don't preventDefault — that blocks the editor from receiving
            // focus, which in turn breaks Slate's keydown pipeline (Backspace
            // / Delete on the void never reach Slate). We just stop bubble
            // to keep the shell's padding-click handler out of the way.
            e.stopPropagation();
            try {
                const ed: any = editor;
                const path = ed?.api?.findPath?.(props.element);
                if (!path) return;
                // Focus FIRST so the editor's DOM has focus when we set the
                // selection — without that, the selection lands but no
                // keyboard events route to Slate.
                ed?.tf?.focus?.();
                ed?.tf?.select?.(path);
            } catch {
                /* defensive: editor / path lookup can fail mid-mutation */
            }
        },
        [editor, props.element],
    );

    // Fallback Backspace/Delete handler. Plate's stock void-deletion path
    // sometimes doesn't fire when the void was selected programmatically
    // (vs via an arrow-key landing on it) — the editor.selection points at
    // the void but Slate's native onKeyDown doesn't always treat that as a
    // "delete this void" trigger. We listen at the document level while the
    // divider is selected and explicitly remove the node on Backspace/Delete.
    useEffect(() => {
        if (!selected) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Backspace' && e.key !== 'Delete') return;
            // Bail if the user is in an input/textarea outside the editor —
            // they might be typing in a comments thread or rename field.
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
            try {
                const ed: any = editor;
                const path = ed?.api?.findPath?.(props.element);
                if (!path) return;
                e.preventDefault();
                ed?.tf?.removeNodes?.({ at: path });
                ed?.tf?.focus?.();
            } catch {
                /* defensive */
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [selected, editor, props.element]);

    return (
        <PlateElement {...props} className={styles.wrapper}>
            <div
                contentEditable={false}
                className={clsx(styles.hrHost, selected && styles.hrHostSelected)}
                onMouseDown={handleSelect}
                role="presentation"
            >
                <hr className={styles.hr} />
            </div>
            {props.children}
        </PlateElement>
    );
}
